import { useEffect, useState } from 'react'
import { startCrossTabSync, type CrossTabChange } from '@/lib/crossTabAuth'
import { resetQueryState } from '@/lib/queryClient'
import { setIdentityStale } from '@/services/api/identityEpoch'

/**
 * Reacts to a login / tenant switch / logout in ANOTHER tab (#301).
 *
 * - Always: mark this tab's identity stale (apiClient then blocks writes),
 *   abort in-flight requests and drop the query cache.
 * - Tenant switch by the same user: return the change so the shell shows a
 *   sticky Reload banner. NO auto-reload in v1: there is no dirty-state
 *   registry, so a reload could discard a half-filled form or a running chat.
 * - Signed out elsewhere: banner (role=alert) with Sign in; the screen is
 *   frozen, not yanked away, so the user can still copy what they were doing.
 * - Different user: go to /login at once (hard navigation). This tab must not
 *   keep showing the previous user's data.
 * Neither calls logout(): that would revoke the other tab's session.
 */
export function useCrossTabIdentity(): CrossTabChange | null {
  const [change, setChange] = useState<CrossTabChange | null>(null)

  useEffect(() => {
    return startCrossTabSync((c) => {
      setIdentityStale(true)
      resetQueryState()
      // Different user: leave at once. Same user (tenant switch) or signed out
      // elsewhere: keep this tab's screen but frozen, with a banner and an action.
      if (c.kind !== 'user') {
        setChange(c)
        return
      }
      window.location.assign('/login')
    })
  }, [])

  return change
}
